import { Primary, Secondary } from '@cinch-labs/design-system'
import cx from 'classnames'
import { useContext } from 'react'

import {
  newCarBrandLinks,
  newCarBrands,
  newCarsFAQs,
  newCarsMonthlyBudgets,
  newCarsSnippets,
} from '../../../data/new-cars'
import { parentLinks } from '../../../data/parent-menu'
import { SharedUiHeaderV2Context } from '../../context/context-shared-ui-header-v2'
import { BrandsList } from '../brands-list/brands-list'
import { ListItem } from '../list-item/list-item'
import { ListSnippetMapper } from '../list-snippet-mapper/list-snippet-mapper'
import { ListWrapper } from '../list-wrapper/list-wrapper'
import styles from './new-cars-menu.module.css'

export interface PartExchangeMenuProps {
  isMobile?: boolean
  handleFaqClick?: (value: string | null) => void
  setShouldShowMobileMenu?: () => void
  showNewCarsUpdates: boolean
}

export const NewCarsMenu = ({
  isMobile,
  handleFaqClick,
  setShouldShowMobileMenu,
  showNewCarsUpdates,
}: PartExchangeMenuProps) => {
  const { logicStore } = useContext(SharedUiHeaderV2Context)

  const { handleTrackEvent } = logicStore

  const onBrandLinkClick = (brandLabel: string, redirectUrl?: string) => {
    handleTrackEvent({
      name: 'click',
      category: 'mega menu',
      label: parentLinks.NEWCARS,
      action: 'shop by type',
      value: brandLabel,
    })

    if (redirectUrl) window.location.assign(redirectUrl)
  }

  return (
    <>
      {showNewCarsUpdates ? (
        <div className={styles.columnsContainer}>
          <nav className={styles.firstColumn}>
            <ListWrapper heading={'Our brands'}>
              <>
                {newCarBrandLinks.map((brand, index) => (
                  <li
                    key={brand.label.replace(/\s/g, '')}
                    className={cx(
                      styles.brandListItem,
                      index + 1 !== newCarBrandLinks.length ? styles.mb10 : '',
                    )}
                  >
                    <img
                      src={brand.imageSrc}
                      height={'40px'}
                      aria-label="hidden"
                      onClick={() => onBrandLinkClick(brand.label, brand.href)}
                    />
                    <ListItem
                      classNames={styles.brandLink}
                      label={brand.label}
                      href={brand.href}
                      handleClickEvent={() => onBrandLinkClick(brand.label)}
                    />
                  </li>
                ))}
              </>
            </ListWrapper>
          </nav>
          {isMobile && <hr className={styles.horizontalLine} />}
          <nav className={styles.secondColumn}>
            <ListWrapper
              heading="By monthly budget"
              urlText="Find the best finance offers"
              href="/new-cars/offers"
              handleTrackEvent={() => {
                handleTrackEvent({
                  name: 'click',
                  category: 'mega menu',
                  label: parentLinks.NEWCARS,
                  action: 'New car finance offers',
                  value: 'Find the best finance offers',
                })
              }}
              hideLinkArrow
            >
              <>
                {newCarsMonthlyBudgets.map((monthlyBudget) => (
                  <li
                    key={monthlyBudget.label}
                    className={styles.monthlyBudgetList}
                  >
                    <ListItem
                      label={monthlyBudget.label}
                      href={monthlyBudget.href}
                      handleClickEvent={() => {
                        handleTrackEvent({
                          name: 'click',
                          category: 'mega menu',
                          label: parentLinks.NEWCARS,
                          action: 'shop by monthly budget',
                          value: monthlyBudget.label,
                        })
                      }}
                    />
                  </li>
                ))}
              </>
            </ListWrapper>
          </nav>
          <Primary
            href="/new-cars"
            element="a"
            isFullWidth
            classNames={{ button: styles.shopAllButton }}
          >
            Shop all new cars
          </Primary>
        </div>
      ) : (
        <>
          <nav className={styles.firstColumn}>
            <ListWrapper heading={'New cars'}>
              <BrandsList brandsList={newCarBrands} />
            </ListWrapper>
            <Secondary href="/new-cars" element="a" isFullWidth>
              Shop all new cars
            </Secondary>
          </nav>
          {isMobile && <hr className={styles.horizontalLine} />}
          <nav className={styles.secondColumn}>
            <ListSnippetMapper
              snippets={newCarsSnippets}
              label={parentLinks.NEWCARS}
              isMobile={isMobile}
            />
          </nav>
        </>
      )}
      <nav className={styles.faqContainer}>
        <ListWrapper
          heading={'FAQs'}
          urlText={'Go to help and support'}
          href={'https://help.cinch.co.uk/hc/en-gb'}
          handleTrackEvent={() =>
            handleTrackEvent({
              name: 'click',
              category: 'mega menu',
              label: parentLinks.NEWCARS,
              action: 'FAQ',
              value: 'Go to support',
            })
          }
          hideLinkArrow
        >
          <>
            {newCarsFAQs.map((faq) => (
              <li key={faq.label} className={styles.faqList}>
                <ListItem
                  label={faq.label}
                  href={faq.href}
                  ariaLabel={faq.ariaLabel}
                  handleClickEvent={() => {
                    handleTrackEvent({
                      name: 'click',
                      category: 'mega menu',
                      label: parentLinks.NEWCARS,
                      action: 'FAQ',
                      value: faq.label,
                    })
                    // handleFaqClick solve the scroll issue on desktop
                    handleFaqClick && handleFaqClick(null)
                    if (setShouldShowMobileMenu) {
                      setShouldShowMobileMenu()
                    }
                  }}
                />
              </li>
            ))}
          </>
        </ListWrapper>
      </nav>
    </>
  )
}
